// @ts-ignore
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { ReactMarkdown } from 'react-markdown/lib/react-markdown';

import { UpdateMsoStockholding, UpdateMsoStockholdingField } from 'psims/models/submission-types/mso/mso-stockholding';
import { UpdateMsoStockholdingOwnership, UpdateMsoStockholdingOwnershipField } from 'psims/models/submission-types/mso/mso-stockholding-ownership';
import { BoxedDiv, BoxedSpan } from 'psims/react/components/layout';
import { Table, TD, TR, ColumnHeader, TH, DeleteActionCellTH } from 'psims/react/pages/primary-pages/data-submissions/shared/data-table-components';
import { H2 } from 'psims/react/components/typography';
import Text from "psims/react/components/text";
import { UseMsoRefData } from './use-mso-ref-data';
import { TooltipHelp } from 'psims/react/pages/primary-pages/data-submissions/shared/tooltip-help';
import Input from '../../shared/input';
import { ModelValidation } from './validation';
import { AddButton, IconButton } from 'psims/react/components/button';
import { isDeleteFocusField, OwnershipFocusField, StockholdingFocusField } from './types';
import { UseConfirm } from '../../shared/use-confirm';
import { DataSubmission, isPopulatedDataSubmission } from 'psims/models/data-submission';
import Select, { useSelectController } from 'psims/react/components/select';
import { recordActionAsEnum, recordActionFromEnum } from 'psims/models/api/data-submission-record-action';
import FloatingMessage from 'psims/react/components/floating-message';
import { EXPIRED_OR_INTERNAL_PRODUCT } from 'psims/constants/validation-messages';
import VisuallyHidden from 'psims/react/components/visually-hidden';
import useFocusable from 'psims/react/util/use-focusable';
import { UsePortalData } from '../../shared/use-portal-data';
import DatepickerInput from 'psims/react/components/datepickerinput';
import { useDashboard } from 'psims/react/providers/api/dashboard';
import useMsoImporterApi, { UseMsoImporterAPI } from '../importer/use-mso-importer-api';
import { useParams } from 'react-router-dom';
import { DataSubmissionRecordActionEnum, MsoStockholdingOwnershipVM, MsoStockholdingVM } from 'psims/gen/xapi-client';

interface MsoStockholdingEditorActions {
    updateStockholding: <TField extends UpdateMsoStockholdingField>(msoProductId: number, field: TField, value: UpdateMsoStockholding[TField]) => any;
    addStockholdingOwnership: (msoProductId: number) => any;
    deleteAllDataForProduct: (msoProductId: number, source: HTMLElement | null) => any;
    deleteStockholdingOwnership: (msoProductId: number, rowIndex: number, source: HTMLElement | null) => any;
    updateStockholdingOwnership: <TField extends UpdateMsoStockholdingOwnershipField>(msoStockholdingId: number, rowIndex: number, field: TField, value: UpdateMsoStockholdingOwnership[TField]) => any
}

type UpdateMsoStockholdingOwnershipView = UpdateMsoStockholdingOwnership & {
    validationMessages: ModelValidation<UpdateMsoStockholdingOwnershipField>
    msoProductId: number;
};

type UpdateMsoStockholdingView = UpdateMsoStockholding & {
    stockholdingOwnerships: Array<UpdateMsoStockholdingOwnershipView>;
    validationMessages: ModelValidation<UpdateMsoStockholdingField>;
};

interface MsoStockholdingEditorProps {
    actions: MsoStockholdingEditorActions;
    deleteCtrl: UseConfirm;
    datasubmission: DataSubmission<'MSO importer' | 'MSO refiner'>;
    focusedField: unknown | null;
    hideEEZ?: boolean;
    isDisabled: boolean;
    msoTypeLabel: 'Importer' | 'Refiner';
    portalData: UsePortalData;
    refData: UseMsoRefData;
    saveAttempted: boolean;
    stockholdings: Array<UpdateMsoStockholdingView>;
}


const HELP_CONTENT_MAP = {
    'density': (
        <BoxedDiv box={{ flex: 'column', width: '300px' }}>
            <p>Report the weighted average density in kilograms per litre (kg/L) for each product during the month. To convert API Gravity to kg/L: Density (kg/L) = 141.5 ÷ (131.5 + API Gravity).</p>
            <p>If the density for each product is not readily available each month, the most recent density measurement should be used. If no measurement is available, a typical or average value may be entered, which should be reviewed and updated as appropriate.</p>
        </BoxedDiv>
    ),
    'holderOwnerStockVolumeImporter': (
        <BoxedDiv box={{ flex: 'column', width: '350px' }}>
            <p>All stock that you own and hold under section 22 of the <em>Fuel Security Act</em> that is being counted towards your MSO (may include all land stock, stock on barges and stock on vessels travelling between domestic ports).</p>
            <p>Note: For stocks held in a Joint Venture (JV) or Joint Terminal (JT) (section 10 of the Rules), the stock must exist and must not be double counted.</p>
            <p>Entities that wish to report under s22 for JV and JT arrangements must have the details specified in your MSO compliance plan.</p>
            <p>Excludes stock that you own being held on behalf of another entity under either s23 or s24 arrangements.</p>
        </BoxedDiv>
    ),
    'holderOwnerStockVolumeRefiner': (
        <BoxedDiv box={{ flex: 'column', width: '350px' }}>
            <p>All stock that you own and hold under section 22 of the <em>Fuel Security Act</em> that is being counted towards your MSO (may include all land stock, stock on barges and stock on vessels travelling between domestic ports).</p>
            <p>Excludes stock that you own being held on behalf of another entity.</p>
        </BoxedDiv>
    ),
    'stockOwnership': (
        <BoxedDiv box={{ flex: 'column', width: '350px' }}>
            <ul>
                <li>
                    <strong>Entitled to take ownership of stock (s23)</strong><br/>Stock that you don't currently own, but you have a legally enforceable arrangement in place which allows you to count it towards your MSO under section 23 of the <em>Fuel Security Act</em>.
                </li>
                <li>
                    <strong>Reserved or quarantined stock (s24)</strong><br/>Stock that you don't own, but you have a legally enforceable arrangement in place which allows you to count it towards your MSO under section 24 of the <em>Fuel Security Act</em> (eg. oil stock tickets).
                </li>
            </ul>

            <br />

            <p>
                A new row will need to be added for each arrangement that is in place.
            </p>
        </BoxedDiv>
    ),
    'arrangementEntity': (
        <BoxedDiv box={{ flex: 'column', width: '350px' }}>
            <p>Name of the entity listed in the legally enforceable arrangement (for section 23 or section 24 of the <em>Fuel Security Act</em>).</p>
        </BoxedDiv>
    ),
    'pipeline': (
        <BoxedDiv box={{ flex: 'column', width: '350px' }}>
            <p>Stock stored within a pipeline that is being counted towards your MSO. It must meet the requirements under section 7 of the Rules and must also be listed in your compliance plan.</p>
        </BoxedDiv>
    ),
     'startDate': (
        <BoxedDiv box={{ flex: 'column', width: '350px' }}>
            <p>Agreement start date</p>
        </BoxedDiv>
    ),
     'endDate': (
        <BoxedDiv box={{ flex: 'column', width: '350px' }}>
            <p>Agreement end date</p>
        </BoxedDiv>
    ),
    'action': (
        <BoxedDiv box={{ flex: 'column', width: '100px' }}>
            <p></p>
        </BoxedDiv>
    ),
    'eez': (
        <BoxedDiv box={{ flex: 'column', width: '350px' }}>
            <p>Stock on a vessel within Australia's exclusive economic zone that is being counted towards your MSO. It must meet the requirements under section 9 of the Rules, with data collection and record keeping details to be listed in your compliance plan.</p>
        </BoxedDiv>
    ),
};

const MsoStockholdingEditor = ({
    actions, datasubmission, deleteCtrl, focusedField, hideEEZ, isDisabled, msoTypeLabel,
    portalData, refData, saveAttempted, stockholdings
}: MsoStockholdingEditorProps) => {
   
    const hasExpiredData = useMemo(() => {
        return stockholdings.some(s => refData.productIdMap[s.msoProductId]?.productStatus === 'inactive');
    }, [refData, stockholdings]);


    function hasStockholdingOwnerships(stockholdings: UpdateMsoStockholdingView[]): boolean {
    if (!stockholdings?.length) return false;

    const first = stockholdings[0];

    return Array.isArray(first.stockholdingOwnerships) && first.stockholdingOwnerships.length > 0;
    }

    const initialHasOwnershipsRef = useRef<boolean | null>(null);

        if (initialHasOwnershipsRef.current === null) {
            initialHasOwnershipsRef.current =
                hasStockholdingOwnerships(stockholdings) &&
                datasubmission.status === 'Not started';
        }

const resultHasStockholdingOwnerships = initialHasOwnershipsRef.current;
    //const resultHasStockholdingOwnerships = hasStockholdingOwnerships(stockholdings) && datasubmission.status === 'Not started';

      const renderedRows = useMemo(() =>
    refData.products.map((product, index) => (
                                <ProductRow
                                    key={index}
                                    actions={actions}
                                    dataSubmissionId={datasubmission.id}
                                    focusedField={focusedField}
                                    hideEEZ={hideEEZ}
                                    isDisabled={isDisabled}
                                    onDelete={deleteCtrl.requestConfirmation}
                                    portalData={portalData}
                                    refData={refData}
                                    product={product}
                                    saveAttempted={saveAttempted}
                                    stockholding={stockholdings.find(s => s.msoProductId === product.id)}
                                    stockholdingIndex={index}
                                    datasubmission={datasubmission}
                                />
    )), [actions, focusedField, hideEEZ, isDisabled, portalData, refData, saveAttempted, stockholdings, deleteCtrl, datasubmission]);


    return (
        <BoxedDiv box={{ flex: 'column' }}>
            <BoxedDiv box={{ marginTop: 6 }}>
                <H2>Stockholding - {msoTypeLabel}</H2>
            </BoxedDiv>
            <BoxedDiv box={{ marginTop: 2 }}>
                <p><Text>This section details any stock you are counting towards your MSO.</Text></p>
                <p><Text>Quantities should be reported to the nearest <strong>megalitre (ML)</strong>.</Text></p>
                {resultHasStockholdingOwnerships && <p><Text $color='green-70'>Stockholding details have been prepopulated based on previous agreement information.</Text></p>}
            </BoxedDiv>

            <BoxedDiv box={{ marginV: 4, overflow: 'auto' }}>
                <Table caption='Data for stockholdings' customWidth='fit-content'>
                    <thead>
                        <TR id='td header'>
                            <ColumnHeader label='MSO Product' fixedWidth='230px' />

                            <ColumnHeader
                                $align='center'
                                fixedWidth='140px'
                                label={<span>Density (kg/L)<br />(optional)</span>}
                                Help={HELP_CONTENT_MAP['density']}
                            />

                            <ColumnHeader
                                $align='center'
                                minimumWidth='140px'
                                label='Holder and owner of stocks (s22)'
                                Help={HELP_CONTENT_MAP[`holderOwnerStockVolume${msoTypeLabel}`]}
                            />

                            <ColumnHeader
                                $align='center'
                                fixedWidth='160px'
                                label='Stock amount'
                            />

                            <ColumnHeader
                                $align='center'
                                fixedWidth='360px'
                                label='Entity the arrangement is with'
                                Help={HELP_CONTENT_MAP['arrangementEntity']}
                            />

                            <ColumnHeader
                                $align='center'
                                fixedWidth='360px'
                                label='Stock ownership'
                                Help={HELP_CONTENT_MAP['stockOwnership']}
                            />
                             <ColumnHeader
                                $align='center'
                                fixedWidth='160px'
                                label='Start date'
                                Help={HELP_CONTENT_MAP['startDate']}
                            />
                             <ColumnHeader
                                $align='center'
                                fixedWidth='160px'
                                label='End date'
                                Help={HELP_CONTENT_MAP['endDate']}
                            />
                             <ColumnHeader
                                $align='center'
                                fixedWidth='90px'
                                label='Action'
                                Help={HELP_CONTENT_MAP['action']}
                            />

                            <ColumnHeader
                                $align='center'
                                fixedWidth='140px'
                                label='Pipeline'
                                Help={HELP_CONTENT_MAP['pipeline']}
                            />
                            {
                                hideEEZ !== true &&
                                <ColumnHeader
                                    $align='center'
                                    fixedWidth='140px'
                                    label='EEZ stock'
                                    Help={HELP_CONTENT_MAP['eez']}
                                />
                            }

                            {
                                hasExpiredData ?
                                    <DeleteActionCellTH>
                                        <VisuallyHidden>Actions</VisuallyHidden>
                                    </DeleteActionCellTH> :
                                    null
                            }
                        </TR>
                    </thead>
                    <tbody>
                      {renderedRows}
                    </tbody>
                </Table>
            </BoxedDiv>
        </BoxedDiv>
    )
}

type OwnershipsProps = {
    actions: MsoStockholdingEditorActions;
    isDisabled: boolean;
    stockholdingIndex: number;
    onDelete: UseConfirm['requestConfirmation'];
    focusedField: unknown | null;
    ownerships: Array<UpdateMsoStockholdingOwnershipView>;
    saveAttempted: boolean;
    product: UseMsoRefData['products'][number];
    datasubmission?: DataSubmission<"MSO importer" | "MSO refiner">;
}

type OwnershipWithStatusFlag =
  Omit<MsoStockholdingOwnershipVM, 'id' | 'concurrencyToken'> & {
    ownershipstatusFlag: 'new' | 'added' | 'deleted';
  };



const Ownerships = (props: OwnershipsProps) => {
    const hasShownAddEntityAlert = useRef(false);
    const [showDateMessage, setShowDateMessage] = useState(false); // ← add this

    const ownerships = props.ownerships
        .map((ownership, rowIndex) => ({
            rowIndex,
            data: ownership,
        }))
        .filter(ownership => ownership.data.msoProductId === props.product.id && ownership.data.recordAction !== recordActionAsEnum('Delete'));

    const handleAddEntity = () => {
        props.actions.addStockholdingOwnership(props.product.id);
        if (!hasShownAddEntityAlert.current) {
            hasShownAddEntityAlert.current = true;
            setShowDateMessage(true); // ← replace alert with this
        }
    }

    return <>
        <TD colSpan={6} style={{ padding: 2 }}>
            <BoxedDiv box={{ flex: 'column' }}>
                {
                    ownerships.map((ownership) => (
                        <Ownership
                            key={`${ownership.data.msoProductId}_${ownership.rowIndex}`}
                            {...props}
                            ownership={ownership}
                            datasubmission={props.datasubmission}
                        />
                    ))
                }

                {/* ← replace alert with this inline message */}
                {showDateMessage && (
                    <BoxedDiv box={{ marginTop: 1 }}> 
                        <Text $color='green-70'>
                           {/* Agreement start and end date is set to current obligation date. For longer term agreement, please update the start and end dates.*/}
                        </Text>
                    </BoxedDiv>
                )} 

                {
                    (!props.isDisabled && props.product.productStatus === 'active') ?
                        <BoxedSpan box={{ alignItems: 'center', flex: 'row', height: '52px' }}>
                            <AddButton
                                label='Add Entity'
                                onClick={handleAddEntity}
                            >Add Entity</AddButton>
                        </BoxedSpan> :
                        null
                }
            </BoxedDiv>
        </TD>
    </>
}

type OwnershipProps = OwnershipsProps & {
    ownership: {
        data: UpdateMsoStockholdingOwnershipView,
        rowIndex: number;
        ownershipStatus?: string;
        ownershipId?: number;
        
    },
    lengthOfArray?:number;
    key?:string;
    datasubmission?: DataSubmission<"MSO importer" | "MSO refiner">
}

const Ownership = ({
    actions,
    focusedField,
    isDisabled,
    ownership,
    onDelete,
    product,
    saveAttempted,
    stockholdingIndex,
    datasubmission,
}: OwnershipProps) => {
    const [deleteRef, setDeleteRef] = useState<HTMLElement | null>(null);
    const [isStartDateTouched, setIsStartDateTouched] = useState(false);
    let submissionTypeName = datasubmission?.submissionTypeName;

    const getLocalDateFormat = (date: string) => {
        const d = new Date(date);

        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");

        return `${year}-${month}-${day}`;
};

    
    const getToday = (): string => {
        const d = new Date();

        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");

        return `${year}-${month}-${day}`;
    };
    //PBI :165784 was assigned to shubie FAROOQUI Post PROD Fix: MSO Weekly 
    // - Allow user to submit data without Start and End date for submissions with obligation date prior to 30/06/2026

    const OBLIGATION_DATE_CUTOFF = new Date('2026-06-30');

    useEffect(() => {
    const obligationDate = datasubmission?.obligationDate;
    if (!obligationDate) return;

    const requiresDate = new Date(obligationDate) >= OBLIGATION_DATE_CUTOFF;

    if (!requiresDate) return; // clarified with Bhuvan not to wipeout any saved dates

    if (!ownership.data.startDate) {
        actions.updateStockholdingOwnership(
            ownership.data.msoProductId,
            ownership.rowIndex,
            'startDate',
            getLocalDateFormat(obligationDate)
        );
    }
    if (!ownership.data.endDate) {
        actions.updateStockholdingOwnership(
            ownership.data.msoProductId,
            ownership.rowIndex,
            'endDate',
            getLocalDateFormat(obligationDate)
        );
    }
    }, [
        ownership.data.startDate,
        ownership.data.endDate,
        ownership.data.msoProductId,
        ownership.rowIndex,
        datasubmission?.obligationDate,
        actions
    ]);
    
    const handleStartDateFocus = () => {
        setIsStartDateTouched(true);
    };


   const handleStartDateChange = useCallback(
    (valueStr: string | undefined) => {
        actions.updateStockholdingOwnership(
            ownership.data.msoProductId,
            ownership.rowIndex,
            'startDate',
            valueStr || null
        );
    }, [actions, ownership.data.msoProductId, ownership.rowIndex]);

    
    const handleEndDateChange = useCallback(
        (valueStr: string | undefined) => {
        actions.updateStockholdingOwnership(
            ownership.data.msoProductId,
            ownership.rowIndex,
            'endDate',
            valueStr || null
        );
        }, [actions, ownership.data.msoProductId, ownership.rowIndex]);

    const obligationDate = datasubmission?.obligationDate;
            const dueDate = datasubmission?.dueDate;
   const dateErrors = useMemo(() => {
    if (!obligationDate) return {};

    const obligation = getLocalDateFormat(new Date(obligationDate).toString());
    const errors: { startDate?: string; endDate?: string } = {};

    const isPriorToCutoff = new Date(obligationDate) < OBLIGATION_DATE_CUTOFF;

    if (ownership.data.startDate) {
        const start = getLocalDateFormat(new Date(ownership.data.startDate).toString());
        if (start > obligation) {
            errors.startDate = 'The agreement start date must start on or before the obligation date.';
        }
    }

    if (ownership.data.endDate) {
        const end = getLocalDateFormat(new Date(ownership.data.endDate).toString());
        if (end < obligation) {
            errors.endDate = 'The agreement end date cannot be before the obligation date.';
        }
    }

    if (ownership.data.startDate && ownership.data.endDate) {
        const start = getLocalDateFormat(new Date(ownership.data.startDate).toString());
        const end = getLocalDateFormat(new Date(ownership.data.endDate).toString());
        if (start > end) {
            errors.startDate = 'The agreement start date must be on or before the agreement end date.';
            errors.endDate = 'The agreement end date must be on or after the agreement start date.';
        }
    }

    // Mandatory pairing — only enforced for obligation dates on/after the cutoff
    if (!isPriorToCutoff) {
        if (ownership.data.startDate && !ownership.data.endDate) {
            errors.endDate = 'The agreement end date is required when a start date has been entered.';
        }
        if (ownership.data.endDate && !ownership.data.startDate) {
            errors.startDate = 'The agreement start date is required when an end date has been entered.';
        }
    }

    return errors;
}, [ownership.data.startDate, ownership.data.endDate, obligationDate]);

    const ownershipFF = useMemo(() => {
        return (
            isOwnershipFocusField(focusedField) &&
            focusedField.msoProductId === ownership.data.msoProductId &&
            focusedField.rowIndex === ownership.rowIndex
        ) ? focusedField : null
            ;
    }, [focusedField, ownership.data.msoProductId, ownership.rowIndex])

    const ownershipSelectCtrl = useSelectController({
        value: ownership.data.msoOwnershipType,
        options: [
            { label: 'Entitled to take ownership of stock (s23)', value: 1 },
            { label: 'Reserved or quarantined stock (s24)', value: 2 },
        ],
        onChange: v => actions.updateStockholdingOwnership(ownership.data.msoProductId, ownership.rowIndex, 'msoOwnershipType', !isNaN(Number(v)) ? Number(v) : undefined)
    })

    return (
        <BoxedDiv box={{ alignItems: 'center', flex: 'row', marginBottom: 1.5 }}>
            <BoxedSpan box={{ alignItems: 'center', flex: 'row', maxWidth: submissionTypeName === 'MSO refiner' ?'170px':'160px', minWidth: submissionTypeName === 'MSO refiner' ?'170px':'160px', paddingRight:1, paddingLeft:1}}>
                <Input
                    autoFocus={ownership.data.id == null}
                    kind='number'
                    disabled={isDisabled}
                    error={ownership.data.validationMessages.stockVolume?.tooltip.message}
                    forceError={saveAttempted}
                    label='Amount held'
                    onChange={v => actions.updateStockholdingOwnership(ownership.data.msoProductId, ownership.rowIndex, 'stockVolume', v)}
                    shouldFocus={ownershipFF?.field === 'stockVolume' && ownershipFF.rowIndex === ownership.rowIndex}
                    value={ownership.data.stockVolume}
                />
            </BoxedSpan>

            <BoxedSpan box={{ maxWidth: submissionTypeName === 'MSO refiner' ?'390px':'360px', minWidth: submissionTypeName === 'MSO refiner' ?'390px':'360px', paddingRight:1, paddingLeft:1  }}>
                <Input
                    kind='string'
                    disabled={isDisabled}
                    error={ownership.data.validationMessages.entityName?.tooltip.message}
                    forceError={saveAttempted}
                    label='Name of entity the arrangement is with'
                    value={ownership.data.entityName}
                    onChange={v => actions.updateStockholdingOwnership(ownership.data.msoProductId, ownership.rowIndex, 'entityName', v)}
                    shouldFocus={ownershipFF?.field === 'entityName'}
                />
            </BoxedSpan>

            <BoxedSpan box={{ maxWidth: submissionTypeName === 'MSO refiner' ?'390px':'360px', minWidth: submissionTypeName === 'MSO refiner' ?'390px':'360px', paddingLeft:1 }}>
                <Select
                    bare={true}
                    borderless={true}
                    error={ownership.data.validationMessages.msoOwnershipType?.tooltip.message}
                    focusless={true}
                    id={`${ownership.data.msoStockholdingId}_${stockholdingIndex}`}
                    isDisabled={isDisabled}
                    label='Stock ownership'
                    options={ownershipSelectCtrl.options}
                    onChange={ownershipSelectCtrl.onChange}
                    setFocused={ownershipFF?.field === 'msoOwnershipType'}
                    useTooltip={true}
                    value={ownershipSelectCtrl.value}
                />
            </BoxedSpan>
            <BoxedSpan box={{ alignItems: 'center', flex: 'row', maxWidth: submissionTypeName === 'MSO refiner' ?'150px':'145px', minWidth: submissionTypeName === 'MSO refiner' ?'150px':'143px', paddingRight:3.5, paddingLeft:1}}>
                {/* allowing for dates to be null to prevent react crashing on null dates */}
                <Input
                    kind="date"
                    disabled={isDisabled}
                    value={ownership.data.startDate ? getLocalDateFormat(ownership.data.startDate) : ''}
                    onChange={handleStartDateChange}
                    error={dateErrors.startDate}
                    forceError={saveAttempted}
                    shouldFocus={false}
                />

            </BoxedSpan>
            <BoxedSpan box={{ alignItems: 'center', flex: 'row', maxWidth: submissionTypeName === 'MSO refiner' ?'160px':'155px', minWidth: submissionTypeName === 'MSO refiner' ?'160px':'152px', paddingRight:2.5, paddingLeft:3 }}>
                 {/* datepicker */}
                {/* allowing for dates to be null to prevent react crashing on null dates */}
                <Input
                    kind="date"
                    disabled={isDisabled}
                    value={ownership.data.endDate ? getLocalDateFormat(ownership.data.endDate) : ''}
                    onChange={handleEndDateChange}
                    error={dateErrors.endDate}
                    forceError={saveAttempted}
                    shouldFocus={false}
                    />
            </BoxedSpan>

            <BoxedSpan box={{ alignItems: 'center', maxWidth:'40px', minWidth:'40px',  paddingLeft:4   }}>
                {
                    // Only show delete button if form is editable and product is active
                    (!isDisabled && product.productStatus === 'active') &&
                    <IconButton
                        color='dark-grey'
                        icon='trash'
                        label='Delete entity'
                        onClick={() => onDelete(
                            'Are you sure you wish to delete this data?',
                            // TODO: fix
                            `${true ? 'Entitled to take ownership' : 'Reserved or quarantined stock'} record will be deleted`,
                            () => actions.deleteStockholdingOwnership(ownership.data.msoProductId, ownership.rowIndex, deleteRef)
                        )}
                        ref={setDeleteRef}
                        size='sm'
                        styles={{ marginLeft: '6px', marginRight: '-12px', padding: '4px' }}
                    />
                }
                
            </BoxedSpan>
        </BoxedDiv>
    )
}

interface ProductRowProps {
    actions: MsoStockholdingEditorActions;
    onDelete: UseConfirm['requestConfirmation'];
    dataSubmissionId: number;
    focusedField: unknown | null;
    hideEEZ?: boolean;
    isDisabled: boolean;
    portalData: UsePortalData;
    product: UseMsoRefData['products'][number];
    refData: UseMsoRefData;
    saveAttempted: boolean;
    stockholding: UpdateMsoStockholdingView | undefined;
    stockholdingIndex: number;
    datasubmission: DataSubmission<"MSO importer" | "MSO refiner">
}

const ProductRow = ({
    actions,
    dataSubmissionId,
    focusedField,
    hideEEZ,
    isDisabled,
    onDelete,
    portalData,
    product,
    refData,
    saveAttempted,
    stockholding: maybeStockholding,
    stockholdingIndex,
    datasubmission,
}: ProductRowProps) => {
    const stockholding = maybeStockholding || {
        dataSubmissionId,
        msoProductId: product.id,
        stockholdingOwnerships: [],
        validationMessages: {} as ModelValidation<UpdateMsoStockholdingField>,
    }

    const [deleteRef, setDeleteRef] = useState<HTMLElement | null>(null);

    const entitledType = useMemo(() => refData.stockOwnershipTypes.find(sot => sot.name === 'Entitled'), [refData]);
    const reservedType = useMemo(() => refData.stockOwnershipTypes.find(sot => sot.name === 'Reserved'), [refData]);

    const stockholdingFF = useMemo(() => (
        isStockholdingFocusField(focusedField) && focusedField.msoProductId === stockholding.msoProductId ? focusedField : null
    ), [focusedField, stockholding.msoProductId])

    if (
        product == null ||
        entitledType == null ||
        reservedType == null ||
        (
            (maybeStockholding == null || recordActionFromEnum(maybeStockholding.recordAction) === 'Delete') && product.productStatus === 'inactive'
        )
    ) {
        // TODO: log missing product
        return null;
    }

    const Help = portalData.productPortalData.find(ppd => ppd?.referenceCode === product.code)?.content

    return (
        <TR>
            <TH scope='row' verticalAlign='top'>
                <BoxedSpan box={{ alignItems: 'center', flex: 'row', width: '220px', height: '52px' }}>
                    <Text $noWrap={true}>{product.name}</Text>
                    {
                        !isDisabled && product.productStatus === 'inactive' ?
                            <BoxedSpan box={{ marginBottom: -0.5, marginLeft: 1 }}>
                                <FloatingMessage
                                    content={EXPIRED_OR_INTERNAL_PRODUCT}
                                    kind="warning"
                                    role="alert"
                                />
                            </BoxedSpan> :
                            Help != null ?
                                <TooltipHelp
                                    Help={<ReactMarkdown>{Help}</ReactMarkdown>}
                                /> :
                                null
                    }
                </BoxedSpan>
            </TH>

            <TD verticalAlign='top'>
                <Input
                    autoFocus={stockholdingIndex === 0}
                    kind='number'
                    disabled={isDisabled}
                    displayFixedDecimals={10}
                    error={stockholding.validationMessages.density?.tooltip.message}
                    forceError={saveAttempted}
                    label='Density (kg/L) (optional)'
                    value={stockholding.density}
                    onChange={v => actions.updateStockholding(stockholding.msoProductId, 'density', v)}
                    shouldFocus={stockholdingFF?.field === 'density'}
                />
            </TD>

            <TD verticalAlign='top'>
                <Input
                    kind='number'
                    disabled={isDisabled}
                    error={stockholding.validationMessages.holderOwnerStockVolume?.tooltip.message}
                    forceError={saveAttempted}
                    label='Holder and owner of stocks (s22)'
                    value={stockholding.holderOwnerStockVolume}
                    onChange={v => actions.updateStockholding(stockholding.msoProductId, 'holderOwnerStockVolume', v)}
                    shouldFocus={stockholdingFF?.field === 'holderOwnerStockVolume'}
                />
            </TD>

            <Ownerships
                actions={actions}
                isDisabled={isDisabled}
                onDelete={onDelete}
                focusedField={focusedField}
                ownerships={stockholding.stockholdingOwnerships}
                product={product}
                saveAttempted={saveAttempted}
                stockholdingIndex={stockholdingIndex}
                datasubmission={datasubmission}
            />

            <TD verticalAlign='top'>
                <Input
                    kind='number'
                    disabled={isDisabled}
                    error={stockholding.validationMessages.pipeline?.tooltip.message}
                    forceError={saveAttempted}
                    label='Pipeline'
                    value={stockholding.pipeline}
                    onChange={v => actions.updateStockholding(stockholding.msoProductId, 'pipeline', v)}
                    shouldFocus={stockholdingFF?.field === 'pipeline'}
                />
            </TD>

            {
                !hideEEZ &&
                <TD verticalAlign='top'>
                    <Input
                        kind='number'
                        disabled={isDisabled}
                        error={stockholding.validationMessages.eez?.tooltip.message}
                        forceError={saveAttempted}
                        label='EEZ stock'
                        value={stockholding.eez}
                        onChange={v => actions.updateStockholding(stockholding.msoProductId, 'eez', v)}
                        shouldFocus={stockholdingFF?.field === 'eez'}
                    />
                </TD>
            }

            {
                !isDisabled && product.productStatus === 'inactive' ?
                    <TD>
                        <DeleteRowButton
                            focusedField={focusedField}
                            onClick={() => onDelete(
                                'All data for this product will be deleted.',
                                'Are you sure you wish to delete this data?',
                                () => actions.deleteAllDataForProduct(product.id, deleteRef)
                            )}
                            productId={product.id}
                            ref={setDeleteRef}
                        />
                    </TD> :
                    null
            }

        </TR>
    );
}

interface DeleteRowProps {
    focusedField: unknown;
    onClick: () => any;
    productId: number;
}

const DeleteRowButton = React.forwardRef(({ focusedField, onClick, productId }: DeleteRowProps, ref) => {
    const focusable = useFocusable({
        setFocused: (
            isDeleteFocusField(focusedField) &&
            focusedField.kind === 'stockholding' &&
            focusedField.msoProductId === productId
        ),
    });

    const setRef = useCallback((el: HTMLElement | null) => {
        if (typeof ref === 'function') {
            ref(el);
        }
        focusable.setRef(el);
    }, [focusable, ref]);

    return (
        <IconButton
            color='dark-grey'
            icon='trash'
            label='Delete row for product'
            onClick={onClick}
            ref={setRef}
            size='sm'
        />
    );
});

export default MsoStockholdingEditor;

function isStockholdingFocusField(maybe?: unknown | null): maybe is StockholdingFocusField {
    if (maybe == null) {
        return false;
    }

    const asF = maybe as StockholdingFocusField;

    return (
        asF.kind === 'stockholding' &&
        asF.field != null &&
        asF.msoProductId != null
    );
}

function isOwnershipFocusField(maybe?: unknown | null): maybe is OwnershipFocusField {
    if (maybe == null) {
        return false;
    }

    const asF = maybe as OwnershipFocusField;

    return (
        asF.kind === 'ownership' &&
        asF.field != null &&
        asF.msoProductId != null &&
        asF.rowIndex != null
    );
}